import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { showSuccessToast, showErrorToast } from "../ui/toast";
import { checkRateLimit, recordAttempt, clearRateLimit, getRemainingAttempts } from "../../lib/rate-limiter";
import { validatePassword, getPasswordRequirements } from "../../lib/password-validator";

interface RegisterFormProps {
  onSuccess?: () => void;
  showLoginLink?: boolean;
  onSwitchToLogin?: () => void;
  referralCode?: string;
}

/**
 * Reusable registration form component
 * Can be used in both modal and standalone page contexts
 */
export function RegisterForm({ onSuccess, showLoginLink = true, onSwitchToLogin, referralCode }: RegisterFormProps) {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    birthday: "",
    agreedToTerms: false,
  });
  const [errors, setErrors] = useState({
    fullName: "",
    email: "",
    password: "",
    birthday: "",
    agreedToTerms: "",
  });
  const [touched, setTouched] = useState({
    fullName: false,
    email: false,
    password: false,
    birthday: false,
    agreedToTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Tracks whether registration succeeded but email verification is pending
  const [pendingVerification, setPendingVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  const { signUp } = useAuth();
  const navigate = useNavigate();

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case "fullName":
        if (!value.trim()) return "Full name is required";
        if (value.trim().length < 2) return "Full name must be at least 2 characters";
        return "";

      case "email":
        if (!value.trim()) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return "Please enter a valid email address";
        return "";

      case "password":
        if (!value) return "Password is required";
        // Use standardized password validation
        const passwordResult = validatePassword(value);
        if (!passwordResult.isValid) return passwordResult.errors[0];
        return "";

      case "birthday":
        if (!value) return "Birthday is required";
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 18) return "You must be at least 18 years old";
        if (age > 120) return "Please enter a valid birthday";
        return "";

      case "agreedToTerms":
        if (!value || value === "false") return "You must agree to the Terms and Privacy Policy";
        return "";

      default:
        return "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check rate limit before attempting signup
    const rateLimitError = checkRateLimit("signup");
    if (rateLimitError) {
      showErrorToast(rateLimitError);
      return;
    }

    // Mark all fields as touched
    setTouched({
      fullName: true,
      email: true,
      password: true,
      birthday: true,
      agreedToTerms: true,
    });

    // Validate all fields
    const newErrors = {
      fullName: validateField("fullName", formData.fullName),
      email: validateField("email", formData.email),
      password: validateField("password", formData.password),
      birthday: validateField("birthday", formData.birthday),
      agreedToTerms: validateField("agreedToTerms", String(formData.agreedToTerms)),
    };

    setErrors(newErrors);

    // Check if any errors exist
    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    if (hasErrors) {
      showErrorToast("Please fix all errors before submitting");
      return;
    }

    setLoading(true);

    try {
      await signUp(formData.email, formData.password, {
        full_name: formData.fullName,
        birthday: formData.birthday,
        role: "customer",
      });

      // FUTURE FEATURE: Voucher Reward Claim (Dormant)
      // TODO: Uncomment when voucher redemption flow is finalized
      /*
      if (referralCode && userId) {
        try {
          const { data: rewardData, error: rewardError } = await supabase.rpc(
            "give_signup_reward",
            {
              p_user_id: userId,
              p_referral_code: referralCode,
            }
          );

          if (!rewardError && rewardData?.success) {
            toast.success(
              `🎉 ${rewardData.voucher.title} claimed! Check your dashboard.`
            );
          }
        } catch (rewardErr) {
          console.error("Reward claim error:", rewardErr);
          // Don't block registration if reward fails
        }
      }
      */

      // Clear rate limit on successful signup
      clearRateLimit("signup");

      // Check if email verification is required by looking for a confirmed session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user?.email_confirmed_at) {
        // Email auto-confirmed (e.g. dev mode) — redirect to dashboard
        showSuccessToast("Account created successfully!", { description: "Redirecting..." });

        if (onSuccess) {
          onSuccess();
        }

        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        // Email verification required — show verification pending message
        setRegisteredEmail(formData.email);
        setPendingVerification(true);
      }
    } catch (err: any) {
      // Record failed attempt for rate limiting
      recordAttempt("signup");
      const remaining = getRemainingAttempts("signup");

      // Handle specific error cases
      let errorMessage = "Failed to create account. Please try again.";

      if (err.message?.includes("already registered") || err.message?.includes("already exists")) {
        errorMessage = "This email is already registered. Please login or use a different email.";
      } else if (err.message?.includes("Invalid email")) {
        errorMessage = "Please enter a valid email address.";
      } else if (err.message?.includes("Password")) {
        errorMessage = getPasswordRequirements();
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Add remaining attempts warning if close to limit
      if (remaining <= 1 && remaining > 0) {
        errorMessage += ` ${remaining} attempt remaining.`;
      }

      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData({
      ...formData,
      [name]: newValue,
    });

    // Clear error when user starts typing/checking
    if (touched[name as keyof typeof touched]) {
      setErrors({
        ...errors,
        [name]: validateField(name, type === "checkbox" ? String(checked) : value),
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
    setErrors({
      ...errors,
      [name]: validateField(name, value),
    });
  };

  // Show email verification pending message after successful registration
  if (pendingVerification) {
    return (
      <div className="text-center space-y-4">
        {/* Green checkmark icon — same pattern as ForgotPassword success state */}
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Check Your Email</h2>

        <p className="text-sm text-gray-600 dark:text-gray-400">We've sent a verification link to:</p>
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{registeredEmail}</p>

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
          Please click the link in your email to verify your account before signing in. If you don't see the email,
          check your spam folder.
        </p>

        <div className="pt-4">
          <Link to="/login" className="text-primary hover:text-primary-dark font-medium underline text-sm">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Referral Code Banner */}
      {referralCode && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-3 py-2 rounded-lg text-xs mb-6">
          🎉 You're using referral code: <strong>{referralCode}</strong>
        </div>
      )}

      {/* Registration Form */}
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <div>
          <label className="block text-base font-medium text-gray-600 dark:text-gray-400 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="fullName"
            placeholder="Jaslin Shah"
            value={formData.fullName}
            onChange={handleChange}
            onBlur={handleBlur}
            className={` p-2 w-full bg-primary/5 ${
              touched.fullName && errors.fullName ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "w-full"
            }`}
          />
          {touched.fullName && errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
        </div>

        <div>
          <label className="block text-base font-medium text-gray-600 dark:text-gray-400 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            placeholder="jaslin.shah@gmail.com"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={` p-2 w-full bg-primary/5 ${
              touched.email && errors.email ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "w-full"
            }`}
          />
          {touched.email && errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-base font-medium text-gray-600 dark:text-gray-400 mb-1">
            Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`pr-10 p-2 w-full bg-primary/5  ${
                touched.password && errors.password
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "w-full"
              }`}
            />
            {/* Show/Hide password toggle button */}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
          {touched.password && errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          {!errors.password && <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">Minimum 8 characters</p>}
        </div>

        <div>
          <label className="block text-base font-medium text-gray-600 dark:text-gray-400 mb-1">
            Birthday <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="birthday"
            value={formData.birthday}
            onChange={handleChange}
            onBlur={handleBlur}
            className={` p-2 w-full bg-primary/5 ${
              touched.birthday && errors.birthday ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "w-full"
            }`}
          />
          {touched.birthday && errors.birthday && <p className="text-red-500 text-xs mt-1">{errors.birthday}</p>}
          {!errors.birthday && <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">You must be 18 or older</p>}
        </div>

        {/* PDPA Consent Checkbox */}
        <div className="space-y-2">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="agreedToTerms"
              name="agreedToTerms"
              checked={formData.agreedToTerms}
              onChange={handleChange}
              onBlur={handleBlur}
              className="mt-1 h-12 w-12 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <label htmlFor="agreedToTerms" className="ml-2 text-sm text-gray-600 dark:text-gray-400">
              I agree to the{" "}
              <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" className="text-primary hover:underline font-medium">
                Privacy Policy
              </Link>
              , and consent to the collection and use of my personal data as described.
              <span className="text-red-500 ml-1">*</span>
            </label>
          </div>
          {touched.agreedToTerms && errors.agreedToTerms && (
            <p className="text-red-500 text-xs ml-6">{errors.agreedToTerms}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3.5 px-8 rounded-pill transition-all duration-200 shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Creating Account..." : "Sign Up"}
        </button>
      </form>

      {/* Login Link */}
      {showLoginLink && (
        <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-6">
          Already have an account?{" "}
          {onSwitchToLogin ? (
            <button onClick={onSwitchToLogin} className="text-primary hover:text-primary-dark font-medium underline">
              Login
            </button>
          ) : (
            <Link to="/login" className="text-primary hover:text-primary-dark font-medium underline">
              Login
            </Link>
          )}
        </p>
      )}
    </>
  );
}
